import styled from '@emotion/styled'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { rgba } from 'emotion-rgba'
import React, {
  ChangeEventHandler,
  FocusEventHandler,
  forwardRef,
  memo,
} from 'react'

export interface Props {
  error?: boolean
  name: string
  isNewsletter?: boolean
  label?: string
  required?: boolean
  onBlur?: FocusEventHandler<HTMLInputElement>
  onChange?: ChangeEventHandler<HTMLInputElement>
  onFocus?: FocusEventHandler<HTMLInputElement>
}

export const Checkbox = memo(
  forwardRef<HTMLInputElement, Props>(function Checkbox(
    {
      error = false,
      name,
      isNewsletter,
      label,
      required,
      onBlur,
      onChange,
      onFocus,
    },
    ref,
  ) {
    const id = `#form-${name}${isNewsletter ? '-newsletter' : ''}`

    return (
      <Container dial={4} row>
        <InputField
          ref={ref}
          id={id}
          name={name}
          type="checkbox"
          required={required}
          onBlur={onBlur}
          onChange={onChange}
          onFocus={onFocus}
        />
        {label ? (
          <Label
            error={error}
            htmlFor={id}
            dangerouslySetInnerHTML={{ __html: label }}
          />
        ) : null}
      </Container>
    )
  }),
)
Checkbox.displayName = 'Checkbox'

const Container = styled(FlexBox)`
  width: 100%;
  color: ${({ theme }) => theme.colors.variants.neutralDark2};
  cursor: pointer;
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1.5rem;
  margin-top: 1.6875rem;
  position: relative;
`

const InputField = styled.input`
  opacity: 0;
  position: absolute;
  z-index: -1;

  &:checked ~ label {
    &:before {
      border-color: ${({ theme }) => theme.colors.variants.neutralDark2};
    }
    &:after {
      opacity: 1;
    }
  }
`

const Label = styled.label<ContainerProps>`
  padding-left: 1.9375rem;
  &:before,
  &:after {
    content: '';
    position: absolute;
  }
  &:before {
    width: 1rem;
    height: 1rem;
    top: 0.25rem;
    left: 0;
    border: 0.125rem solid
      ${({ error, theme }) =>
        error
          ? theme.colors.variants.dangerLight
          : rgba(theme.colors.variants.neutralDark2, 0.3)};
  }
  &:after {
    width: 0.5rem;
    height: 0.5rem;
    top: 0.5rem;
    left: 0.25rem;
    background: ${({ error, theme }) =>
      error
        ? theme.colors.variants.dangerLight
        : theme.colors.variants.neutralDark2};
    opacity: 0;
  }

  a {
    text-decoration: underline;
  }
`

interface ContainerProps {
  error: boolean
}
